import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToastStore } from '../../../stores/toastStore';
import type { TenantAdminSummaryResponse, TenantStatus } from '../api';
import { ChangeLicenseModal } from '../components/ChangeLicenseModal';
import { CreateTenantModal } from '../components/CreateTenantModal';
import { PlanBadge } from '../components/PlanBadge';
import { TenantActionsMenu } from '../components/TenantActionsMenu';
import { TenantStatusBadge } from '../components/TenantStatusBadge';
import { useAdminTenants, useChangeTenantLicense, useCreateTenant, useUpdateTenantStatus } from '../hooks/useAdminTenants';
import { useLicensePlans } from '../hooks/useLicensePlans';

const PAGE_SIZE = 20;

export function TenantsListPage(): JSX.Element {
  const navigate = useNavigate();
  const pushToast = useToastStore((s) => s.push);

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | ''>('');
  const [licenseFilter, setLicenseFilter] = useState<string>('');
  const [createOpen, setCreateOpen] = useState(false);
  const [changeLicenseTenant, setChangeLicenseTenant] = useState<TenantAdminSummaryResponse | null>(null);

  const filter: Parameters<typeof useAdminTenants>[0] = { page, pageSize: PAGE_SIZE };
  if (search) filter.search = search;
  if (statusFilter) filter.status = statusFilter;
  if (licenseFilter) filter.licenseType = licenseFilter;
  const { data, isLoading, isError } = useAdminTenants(filter);

  const { data: plans } = useLicensePlans();
  const createMutation = useCreateTenant();
  const statusMutation = useUpdateTenantStatus();
  const licenseMutation = useChangeTenantLicense();

  const handleStatusChange = (id: string, newStatus: TenantStatus): void => {
    statusMutation.mutate(
      { id, request: { newStatus } },
      {
        onSuccess: () => pushToast({ tone: 'success', title: 'Estado actualizado' }),
        onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
      },
    );
  };

  const handleLicenseChange = (newLicense: string, expiresAt: string): void => {
    if (changeLicenseTenant === null) return;
    licenseMutation.mutate(
      { id: changeLicenseTenant.tenantId, request: { newLicense, expiresAt } },
      {
        onSuccess: () => { pushToast({ tone: 'success', title: 'Plan actualizado' }); setChangeLicenseTenant(null); },
        onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
      },
    );
  };

  const totalPages = data !== undefined ? Math.ceil(data.totalCount / PAGE_SIZE) : 1;

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Tenants</h1>
          {data !== undefined ? (
            <p className="mt-0.5 text-sm text-slate-500">{data.totalCount.toLocaleString('es-ES')} tenants registrados</p>
          ) : null}
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
          Crear tenant
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder="Buscar…"
          className="input w-full sm:max-w-xs"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="input w-full sm:w-36" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as TenantStatus | ''); setPage(1); }}>
          <option value="">Todos los estados</option>
          <option value="Active">Activo</option>
          <option value="Suspended">Suspendido</option>
          <option value="Blocked">Bloqueado</option>
          <option value="Pending">Pendiente</option>
        </select>
        <select className="input w-full sm:w-40" value={licenseFilter} onChange={(e) => { setLicenseFilter(e.target.value); setPage(1); }}>
          <option value="">Todos los planes</option>
          {(plans ?? []).map((p) => (
            <option key={p.licenseType} value={p.licenseType}>{p.displayName}</option>
          ))}
        </select>
      </div>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudo cargar la lista. Refresca para reintentar.</div>
      ) : null}

      <div className="card overflow-hidden">
        <table className="hidden w-full text-sm md:table">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Tenant</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Estado</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Plan</th>
              <th className="px-4 py-3 text-right font-medium text-slate-700">Docs/mes</th>
              <th className="px-4 py-3 text-right font-medium text-slate-700">Expiración</th>
              <th className="w-10 px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 animate-pulse rounded bg-slate-100" />
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              (data?.items ?? []).map((tenant) => (
                <tr key={tenant.tenantId} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{tenant.name}</p>
                    <p className="font-mono text-xs text-slate-500">{tenant.tenantId}</p>
                  </td>
                  <td className="px-4 py-3"><TenantStatusBadge status={tenant.status} /></td>
                  <td className="px-4 py-3"><PlanBadge plan={tenant.licenseType} /></td>
                  <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                    {tenant.monthlyDocumentCount.toLocaleString('es-ES')}
                    {tenant.monthlyDocumentLimit !== null ? (
                      <span className="text-slate-400"> / {tenant.monthlyDocumentLimit.toLocaleString('es-ES')}</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500">
                    {new Date(tenant.licenseExpiresAt).toLocaleDateString('es-ES')}
                  </td>
                  <td className="px-4 py-3">
                    <TenantActionsMenu
                      tenant={tenant}
                      onChangeStatus={handleStatusChange}
                      onChangeLicense={setChangeLicenseTenant}
                      onView={(id) => void navigate(`/admin/tenants/${id}`)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Mobile card list — same data as the table rows. */}
        <ul className="divide-y divide-slate-100 md:hidden">
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <li key={i} className="p-4">
                  <div className="h-4 animate-pulse rounded bg-slate-100" />
                </li>
              ))
            : (data?.items ?? []).map((tenant) => (
                <li key={tenant.tenantId} className="space-y-2 p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">{tenant.name}</p>
                      <p className="truncate font-mono text-xs text-slate-500">{tenant.tenantId}</p>
                    </div>
                    <TenantActionsMenu
                      tenant={tenant}
                      onChangeStatus={handleStatusChange}
                      onChangeLicense={setChangeLicenseTenant}
                      onView={(id) => void navigate(`/admin/tenants/${id}`)}
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <TenantStatusBadge status={tenant.status} />
                    <PlanBadge plan={tenant.licenseType} />
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className="tabular-nums">
                      Docs/mes: {tenant.monthlyDocumentCount.toLocaleString('es-ES')}
                      {tenant.monthlyDocumentLimit !== null
                        ? ` / ${tenant.monthlyDocumentLimit.toLocaleString('es-ES')}`
                        : ''}
                    </span>
                    <span>Expira: {new Date(tenant.licenseExpiresAt).toLocaleDateString('es-ES')}</span>
                  </div>
                </li>
              ))}
        </ul>

        {data !== undefined && totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
            <span className="text-xs text-slate-500">
              Página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Anterior</button>
              <button type="button" className="btn-secondary px-2 py-1 text-xs" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Siguiente</button>
            </div>
          </div>
        ) : null}
      </div>

      <CreateTenantModal
        open={createOpen}
        busy={createMutation.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={(req) => {
          createMutation.mutate(req, {
            onSuccess: () => { pushToast({ tone: 'success', title: 'Tenant creado' }); setCreateOpen(false); },
            onError: (err) => pushToast({ tone: 'error', title: 'Error al crear tenant', description: err.message }),
          });
        }}
      />

      <ChangeLicenseModal
        tenant={changeLicenseTenant}
        busy={licenseMutation.isPending}
        onClose={() => setChangeLicenseTenant(null)}
        onSubmit={handleLicenseChange}
      />
    </div>
  );
}
